/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

/**
 * Includes draftail
 * https://www.draftail.org/
 * Copyright Springload. Licensed under the MIT License
 * See license text at https://github.com/springload/draftail/blob/master/LICENSE
 *
 * Includes draft-js
 * https://draftjs.org/
 * Copyright Facebook. Licensed under the MIT License
 * See license text at https://github.com/facebook/draft-js/blob/master/LICENSE
 *
 * Includes draft-convert
 * https://github.com/HubSpot/draft-convert
 * Copyright HubSpot. Licensed under Apache-2.0
 * See license text at https://github.com/HubSpot/draft-convert/blob/master/LICENSE
 *
 * Includes draftjs-conductor
 * https://github.com/thibaudcolas/draftjs-conductor
 * Copyright Thibaud Colas. Licensed under the MIT License
 * See license text at https://github.com/thibaudcolas/draftjs-conductor/blob/master/LICENSE
 *
 * Includes draft-js-import-html
 * https://github.com/sstur/draft-js-utils
 * Copyright Simon Sturmer. Licensed under ISC
 * See license text at https://github.com/sstur/draft-js-utils/blob/master/LICENSE
 *
 */
import React, { Component } from 'react';

import { convertFromHTML, convertToHTML } from 'draft-convert';
import { CompositeDecorator, EditorState, convertFromRaw } from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';
import 'draft-js/dist/Draft.css';
import { DraftailEditor } from 'draftail';
import { serialiseEditorStateToRaw } from 'draftjs-conductor';

import { InputUtils, StringUtils } from '@qality/common-utils';

import { EditorWrapper, ToolbarWrapper } from './CoreEditor.styled';
import EditorToolbar from './EditorToolbar';
import EditorUtils, { BLOCK_CONTROL, INLINE_STYLES } from './EditorUtils';
import EventEmitter from './EventEmitter';
import LinkDecorator from './LinkDecorator';
import './style.css';

const BLOCK_LISTS = [
  BLOCK_CONTROL.UNORDERED_LIST_ITEM,
  BLOCK_CONTROL.ORDERED_LIST_ITEM,
];

type Props = {
  defaultOptions: Array<any>;
  listEnabled: boolean;
  isFocused: boolean;
  value: string;
  onChange: (html: string) => void;
  onSave: (html: string) => void;
  onBlur: () => void;
  onClick: () => void;
  placeholder: string;
  shouldLoadToolbar: boolean;
  setOriginalText: (html: string) => void;
};

class CoreEditor extends Component<Props> {
  htmlContent = '';
  editorRef = React.createRef();
  newEditorState = null;
  shouldAddMissingCharacters = false;
  constructor(props) {
    super(props);
    EventEmitter.subscribe('space_pressed', () => this.handleSpacePressed());
    EventEmitter.subscribe('k_pressed', () => this.handleKeyPressed('k'));
    EventEmitter.subscribe('j_pressed', () => this.handleKeyPressed('j'));
    this.blockTypes = props.listEnabled ? BLOCK_LISTS : [];
    this.state = {
      rawContent: null,
      setFocus: false,
      showEditor: true,
      editorState: EditorState.createEmpty(
        new CompositeDecorator([LinkDecorator])
      ),
    };
  }

  static defaultProps = {
    shouldLoadToolbar: true,
  };

  componentDidMount = async () => {
    if (!StringUtils.isNilOrEmpty(this.props.value)) {
      const contentState = convertFromHTML(
        this.props.value.replace(/ <\/p>$/g, '&nbsp;</p>'),
        { flat: true }
      );

      const initEditorState = EditorState.createWithContent(
        contentState,
        new CompositeDecorator([LinkDecorator])
      );

      this.setState({
        editorState: initEditorState,
      });

      this.setOriginalText(initEditorState);
    }
    // without this links does not work
    await this.sleep(100);
    if (this.props.isFocused) {
      this.editorRef.current?.focus();
    }
  };

  setOriginalText = (initEditorState: () => void) => {
    if (this.props.setOriginalText) {
      const content = serialiseEditorStateToRaw(initEditorState);
      this.props.setOriginalText(this.toHTML(content));
    }
  };

  componentDidUpdate = async () => {
    if (this.state.setFocus && this.state.showEditor) {
      // without this links does not work
      await this.sleep(100);
      this.editorRef.current.focus();
      this.setState({
        setFocus: false,
      });
    }

    if (!this.state.showEditor) {
      this.setState({
        showEditor: true,
      });
    }
  };

  sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  updateEditor = (setFocus = false) => {
    this.setState({
      setFocus,
      showEditor: false,
    });
  };

  // eslint-disable-next-line complexity
  handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      this.save();
      if (e.preventDefault) {
        e.preventDefault();
      }
      if (e.stopPropagation) {
        e.stopPropagation();
      }
    }

    // Firefox for some reason has multiple focuses.
    // When editor tries to add selection/carret to where user clicked it's bugged,
    // probably because of multiple focuses.
    // To avoid this blur the editor before moving to the next.
    if (e.key === 'Tab' && navigator.userAgent.includes('Firefox')) {
      // Avoid blur when user is using lists and want to add nested list
      const currentBlock = this.state.editorState
        .getCurrentContent()
        .getBlockForKey(this.state.editorState.getSelection().getStartKey())
        .getType();
      if (
        currentBlock !== 'ordered-list-item' &&
        currentBlock !== 'unordered-list-item'
      ) {
        this.props.onBlur();
      }
    }
  };

  handleSpacePressed = () => {
    this.newEditorState = EditorUtils.getFixedEditorStateForServiceDeskView(
      this.state.editorState,
      this.blockTypes
    );
  };

  handleKeyPressed = async (char) => {
    if (this.state.editorState.getSelection().getHasFocus()) {
      this.shouldAddMissingCharacters = true;
      await this.sleep(100);
      if (this.shouldAddMissingCharacters) {
        this.setState({
          editorState: EditorUtils.addMissingCharactersToEditorInput(
            this.state.editorState,
            char
          ),
        });
      }
    }
  };

  handleChange = (editorState) => {
    this.shouldAddMissingCharacters = false;
    let stateForOnChange = editorState;
    if (this.newEditorState && this.newEditorState !== editorState) {
      stateForOnChange = this.newEditorState;
      this.setState({
        editorState: this.newEditorState,
      });
    } else {
      this.setState({
        editorState: editorState,
      });
    }
    this.newEditorState = null;

    if (this.props.onChange) {
      const content = serialiseEditorStateToRaw(stateForOnChange);
      this.props.onChange(this.toHTML(content));
    }
  };

  clear = () => {
    const contentState = stateFromHTML('');

    this.setState({
      editorState: EditorState.createWithContent(
        contentState,
        new CompositeDecorator([LinkDecorator])
      ),
    });
    this.updateEditor(false);
  };

  save = () => {
    if (this.props.onSave) {
      const editorState = this.state.editorState;
      const content = serialiseEditorStateToRaw(editorState);
      const success = this.props.onSave(this.toHTML(content));
      if (success) {
        this.setState({
          editorState: EditorState.createEmpty(
            new CompositeDecorator([LinkDecorator])
          ),
        });
        this.updateEditor(true);
      }
    }
  };

  toHTML = (raw) => {
    const text = raw ? convertFromRaw(raw) : '';
    let data = text
      ? convertToHTML({
          styleToHTML: (style) => {
            if (style === 'STRIKETHROUGH') {
              return <strike />;
            }
          },
        })(text)
      : '';

    data = InputUtils.keepEmptyLines(data);
    return LinkDecorator.addClickableLinks(data);
  };

  showPlaceholder = () => {
    if (!this.state.editorState.getSelection().getHasFocus()) {
      const editorState = this.state.editorState;
      const content = serialiseEditorStateToRaw(editorState);
      return StringUtils.isNilOrEmpty(content);
    }
    return false;
  };

  handleFocus = () => {
    // NOTE: queueMicrotask is required here. Otherwise it will not work
    queueMicrotask(() => {
      this.setState({
        editorState: EditorState.moveFocusToEnd(this.state.editorState),
      });
    });
    this.props.onClick();
  };

  // eslint-disable-next-line complexity
  render() {
    return (
      <div onKeyDown={this.handleKeyDown}>
        {this.showPlaceholder() && (
          <span className='editor-placeholder'>{this.props.placeholder}</span>
        )}
        {this.state.showEditor && (
          <EditorWrapper>
            <DraftailEditor
              onChange={this.handleChange}
              editorState={this.state.editorState}
              ref={this.editorRef}
              topToolbar={null}
              onBlur={this.props.onBlur}
              onFocus={this.handleFocus}
              decorators={[LinkDecorator]}
              inlineStyles={INLINE_STYLES}
              blockTypes={this.blockTypes}
              spellCheck
            />
            {this.props.shouldLoadToolbar && this.props.isFocused && (
              <ToolbarWrapper>
                <EditorToolbar
                  editorState={this.state.editorState}
                  handleChange={this.handleChange}
                />
              </ToolbarWrapper>
            )}
          </EditorWrapper>
        )}
      </div>
    );
  }
}

export default CoreEditor;
